import React from 'react';
import Modal from '../Modal';
import FieldRenderer from './FieldRenderer';
import { ReportFieldValue, ReportFormValues, ReportSchema } from '../../types';
import './ReportForm.css';

interface ReportFormModalProps {
  isOpen: boolean;
  onCancel: () => void;
  // From the report list; the schema itself has no name.
  reportName: string;
  schema: ReportSchema | null;
  schemaStatus: 'idle' | 'loading' | 'ready' | 'error';
  values: ReportFormValues;
  fieldErrors: Record<string, string>;
  // True while a chat reply is still coming in; the form is sent as a chat message, so it waits too.
  submitDisabled?: boolean;
  onFieldChange: (fieldKey: string, value: ReportFieldValue) => void;
  onSubmit: () => void;
}

// Draws any report's form from its schema; a new report needs no change here.
// Fields arrive already sorted by display_order from the API.
const ReportFormModal: React.FC<ReportFormModalProps> = ({
  isOpen,
  onCancel,
  reportName,
  schema,
  schemaStatus,
  values,
  fieldErrors,
  submitDisabled = false,
  onFieldChange,
  onSubmit,
}) => {
  const errorCount = Object.keys(fieldErrors).length;

  return (
    <Modal isOpen={isOpen} onClose={onCancel} ariaLabel={`${reportName} form`} maxWidth={560}>
      <div className="report-modal">
        <div className="report-modal-header">
          <div className="report-modal-title">{reportName}</div>
          <button type="button" className="report-modal-close" onClick={onCancel} aria-label="Close" title="Close">
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>

        <div className="report-modal-body">
          {schemaStatus === 'loading' && (
            <div className="focus-loading">
              <i className="fas fa-spinner fa-spin" /> Loading form…
            </div>
          )}

          {schemaStatus === 'error' && <div className="focus-error">Couldn't load this report form.</div>}

          {schemaStatus === 'ready' &&
            schema?.fields.map((field) => (
              <FieldRenderer
                key={field.field_key}
                field={field}
                value={values[field.field_key] ?? null}
                error={fieldErrors[field.field_key]}
                onChange={(v) => onFieldChange(field.field_key, v)}
              />
            ))}
        </div>

        {schemaStatus === 'ready' && (
          <div className="box-modal-footer report-modal-footer">
            <span className="box-footer-summary">
              {errorCount === 1 ? '1 field needs attention' : errorCount > 1 ? `${errorCount} fields need attention` : ''}
            </span>
            <div className="box-footer-actions">
              <button type="button" className="box-btn-cancel" onClick={onCancel}>
                Cancel
              </button>
              <button
                type="button"
                className="box-btn-submit"
                onClick={onSubmit}
                disabled={submitDisabled}
                title={submitDisabled ? 'Wait for the current reply to finish' : undefined}
              >
                Submit
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ReportFormModal;
